import React, { useState, useEffect, useRef } from 'react';
import { FaFileAlt, FaUpload, FaTimes } from 'react-icons/fa';
import { showNotificationGlobal } from '../common/NotificationProvider';
import { MAX_FILE_SIZE } from '../../settings/shared/constants';

// File Upload Component for file type parameters
const FileUploadComponent = ({ value, onChange, maxFiles = 5, accept }) => {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState(() => {
    try {
      if (value && value.trim()) {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      // Invalid JSON, treat as empty
    }
    return [];
  });

  // Update parent when files change
  useEffect(() => {
    const valueStr = files.length > 0 ? JSON.stringify(files) : '';
    if (valueStr !== value) {
      onChange(valueStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const currentCount = files.length;
    const newCount = selectedFiles.length;

    // Check max file count
    if (currentCount + newCount > maxFiles) {
      showNotificationGlobal(
        `Maximum ${maxFiles} files allowed. Remove some files first.`,
        'error',
        'topCenter'
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file size (3MB max) and file type (if accept prop is provided)
    const invalidFiles = [];

    selectedFiles.forEach((file) => {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        invalidFiles.push(`${file.name} (exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit)`);
        return;
      }

      // Check file type if accept prop is provided
      if (accept) {
        const acceptedTypes = accept.split(',').map(type => type.trim());
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        const fileType = file.type.toLowerCase();
        
        const isAccepted = acceptedTypes.some(acceptedType => {
          const normalizedAccepted = acceptedType.toLowerCase();
          // Check MIME type match (e.g., "image/*", "application/pdf")
          if (normalizedAccepted.includes('*')) {
            const baseType = normalizedAccepted.split('/')[0];
            return fileType.startsWith(baseType + '/');
          }
          // Check exact MIME type match
          if (normalizedAccepted === fileType) {
            return true;
          }
          // Check file extension match (e.g., ".pdf", ".jpg")
          if (normalizedAccepted.startsWith('.') && normalizedAccepted === fileExtension) {
            return true;
          }
          return false;
        });

        if (!isAccepted) {
          invalidFiles.push(`${file.name} (file type not accepted)`);
        }
      }
    });

    // Show error if any files are invalid
    if (invalidFiles.length > 0) {
      showNotificationGlobal(
        `Invalid files: ${invalidFiles.join(', ')}`,
        'error',
        'topCenter'
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Process files and store as base64
    const filePromises = selectedFiles.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result;
          const base64 = dataUrl.split(',')[1];
          resolve({
            name: file.name,
            size: file.size,
            type: file.type,
            base64: base64,
            dataUrl: dataUrl
          });
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(filePromises).then((newFiles) => {
      const validFiles = newFiles.filter(f => f !== null);
      if (validFiles.length > 0) {
        setFiles(prev => [...prev, ...validFiles]);
      }
    });

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return <i className="fa-solid fa-image" style={{ fontSize: '14px', color: '#3b82f6' }}></i>;
    } else if (fileType === 'application/pdf') {
      return <i className="fa-solid fa-file-pdf" style={{ fontSize: '14px', color: '#ef4444' }}></i>;
    } else if (fileType.includes('text') || fileType.includes('json') || fileType.includes('xml')) {
      return <i className="fa-solid fa-file-lines" style={{ fontSize: '14px', color: '#10b981' }}></i>;
    } else if (fileType.includes('zip') || fileType.includes('compressed')) {
      return <i className="fa-solid fa-file-zipper" style={{ fontSize: '14px', color: '#f59e0b' }}></i>;
    }
    return <FaFileAlt size={14} style={{ color: '#6b7280' }} />;
  };

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: '100%', 
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      <button
        type="button"
        onClick={() => {
          if (files.length >= maxFiles) {
            showNotificationGlobal(
              `Maximum ${maxFiles} files allowed. Remove some files first.`,
              'error',
              'topCenter'
            );
            return;
          }
          fileInputRef.current?.click();
        }}
        disabled={files.length >= maxFiles}
        style={{
          width: '100%',
          maxWidth: '100%',
          padding: '6px 12px',
          border: '1px solid #d1d5db',
          borderRadius: 4,
          fontSize: 11,
          background: files.length >= maxFiles ? '#f3f4f6' : '#ffffff',
          color: files.length >= maxFiles ? '#9ca3af' : '#374151',
          cursor: files.length >= maxFiles ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          transition: 'all 0.2s',
          fontWeight: 500,
          boxSizing: 'border-box',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        onMouseOver={(e) => {
          if (files.length < maxFiles) {
            e.target.style.background = '#f9fafb';
            e.target.style.borderColor = '#3b82f6';
          }
        }}
        onMouseOut={(e) => {
          if (files.length < maxFiles) {
            e.target.style.background = '#ffffff';
            e.target.style.borderColor = '#d1d5db';
          }
        }}
      >
        <FaUpload size={10} />
        <span>Choose Files {files.length > 0 && `(${files.length}/${maxFiles})`}</span>
      </button>

      {files.length > 0 && (
        <div style={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          {files.map((file, index) => {
            const isImage = file.type.startsWith('image/');
            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 8px',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  fontSize: 10,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 6, 
                  flex: 1, 
                  minWidth: 0,
                  width: 0,
                  overflow: 'hidden'
                }}>
                  {getFileIcon(file.type)}
                  <div style={{ 
                    flex: 1, 
                    minWidth: 0, 
                    overflow: 'hidden',
                    width: 0
                  }}>
                    <div style={{
                      fontWeight: 500,
                      color: '#374151',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: 10,
                      width: '100%'
                    }} title={file.name}>
                      {file.name}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: 9 }}>
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '2px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    transition: 'background 0.2s',
                    flexShrink: 0,
                    minWidth: '20px'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#fee2e2'}
                  onMouseOut={(e) => e.target.style.background = 'transparent'}
                  title="Remove file"
                >
                  <FaTimes size={8} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FileUploadComponent;
